/**
 * FinalExitDetails Component
 *
 * Container component for Final Exit process
 * Uses SettlementDetails for UI + adds action buttons
 * Production-grade with proper state management and error handling
 */

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { HiX, HiBan, HiCheckCircle, HiUpload, HiDownload } from "react-icons/hi";
import nodeinstance from "../../../../../nodeInstance";
import { setAppError } from "../../../../../store/userSlice";
import SettlementDetails from "../SettlementDetails/SettlementDetails";
import FinalExitProceed from "../ExitFromUserDetails/finalexitPopUp";
import CancelPopUp from "../ExitFromUserDetails/cancelPopUp";

const FinalExitDetails = ({
  employeeID,
  updateAPI,
  setAddOrEdit,
  addOrEdit,
  repatration,
  setIndex,
  updateNew,
  redirectPath = "/dashboard/final-exit",
  isLocalExit = false,
}) => {
  // State Management
  const [data, setData] = useState();
  const [trigger, setTrigger] = useState(false);
  const [repatriationAmount, setRepatriationAmount] = useState(0);
  const [repatriationSaved, setRepatriationSaved] = useState(false);
  const [selectedRepatrationType, setSelectedRepatrationType] = useState(null);
  const [fandftrigger, setFandfTrigger] = useState(false);
  const [finalClick, setFinalClick] = useState(false);
  const [cancel, setCancel] = useState(false);
  const [propData, setPropData] = useState();
  const [file, setFile] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  const token = localStorage.getItem("nodeToken");
  const baseEndpoint = isLocalExit ? '/localexit' : '/mobilization';
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Fetch employee data
  const fetchEmployeeData = async () => {
    try {
      console.log("🔍 FinalExitDetails - Fetching data for employeeID:", employeeID);

      const endpoint = isLocalExit
        ? `/localexit/localExitFormalities/${employeeID}`
        : `/mobilization/finalExitFormalities/${employeeID}`;

      const response = await nodeinstance.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 200) {
        console.log("✅ FinalExitDetails - API Response received:", response.data.data);
        console.log("📊 Contract History from API:", response.data.data?.contractHistory);
        setData({...response.data.data, documents: JSON.parse(response.data.data.documents) });

        // Initialize repatriation state if data exists
        const repatrationData = response.data.data;
        if (repatrationData?.repatration_Status === "DONE" && repatrationData?.data?.[0]?.repatrationAmount) {
          setRepatriationAmount(repatrationData.data[0].repatrationAmount);
          setRepatriationSaved(true);
          setSelectedRepatrationType(repatrationData.repatrationType);
        }
      }
    } catch (error) {
      console.error("Error fetching employee data:", error);
      dispatch(setAppError({ msg: "Failed to fetch employee data" }));
    }
  };

  useEffect(() => {
    fetchEmployeeData();
  }, [employeeID, trigger]);

  // Action Handlers
  const handleCancelClick = () => {
    setCancel(true);
    setPropData(data);
  };

  const handleProceedClick = async () => {
    // Check if repatriation is saved before proceeding
    if (!repatriationSaved && selectedRepatrationType) {
      alert("Please save repatriation data first before proceeding to final exit.");
      return;
    }
    setFandfTrigger(true);
  };

  const handleRejectClick = async () => {
    // Check if repatriation is saved before rejecting
    if (!repatriationSaved && selectedRepatrationType) {
      alert("Please save repatriation data first before rejecting entry.");
      return;
    }

    try {
      const obj = { employee_id: employeeID };
      const response = await nodeinstance.put(`/mobilization/opsrejectfinal`, obj, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 200) {
        localStorage.setItem("final", "true");
        navigate(redirectPath);
      }
    } catch (error) {
      console.error("Error rejecting entry:", error);
      dispatch(setAppError({ msg: "Failed to reject entry" }));
    }
  };

  const handleFileUpload = async () => {
    if (!file) {
      dispatch(setAppError({ msg: "Please select a file" }));
      return;
    }

    setIsUploading(true);
    try {
      const endPoint = isLocalExit
        ? `/localexit/uploadBulkDoc/${employeeID}`
        : `/mobilization/uploadBulkDoc/${employeeID}`;

      const formData = new FormData();
      formData.append("files", file);

      const response = await nodeinstance.post(endPoint, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.status === 200) {
        navigate(redirectPath);
        dispatch(setAppError({ msg: "Uploaded successfully", color: "success" }));
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      dispatch(setAppError({ msg: "Failed to upload file" }));
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileDownload = async () => {
    try {
      const downloadEndpoint = isLocalExit
        ? `/localexit/downloadDocs/${employeeID}`
        : `/mobilization/downloadDocs/${employeeID}`;

      const response = await nodeinstance.get(downloadEndpoint, {
        responseType: "blob",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 200) {
        const blob = new Blob([response.data], { type: "application/zip" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "finalexitdocument.zip");
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Error downloading files:", error);
      dispatch(setAppError({ msg: "Failed to download files" }));
    }
  };

  const isOpsAuthorized = data?.Final_exit_status === "OPSAUTHORISED";

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Settlement Details UI */}
      <SettlementDetails
        employeeID={employeeID}
        data={data}
        trigger={trigger}
        setTrigger={setTrigger}
        isLocalExit={isLocalExit}
        baseEndpoint={baseEndpoint}
        repatration={repatration}
        updateNew={updateNew}
        repatriationAmount={repatriationAmount}
        setRepatriationAmount={setRepatriationAmount}
        repatriationSaved={repatriationSaved}
        setRepatriationSaved={setRepatriationSaved}
        selectedRepatrationType={selectedRepatrationType}
        setSelectedRepatrationType={setSelectedRepatrationType}
        fandftrigger={fandftrigger}
        setFandfTrigger={setFandfTrigger}
        isPreviewMode={false}
      />

      {/* Action Buttons Section */}
      {!isLocalExit && (
        <div className="flex items-center justify-between mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          {/* Cancel Button */}
          <button
            onClick={handleCancelClick}
            className="inline-flex items-center px-6 py-2.5 bg-btn-dark text-white font-semibold text-xs uppercase rounded-lg hover:bg-btn-dark-hover focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-150 shadow-md hover:shadow-lg transform hover:scale-[1.02] active:scale-[0.98]"
          >
            <HiX className="w-4 h-4 mr-2" />
            CANCEL FINAL EXIT
          </button>

          {/* Right Side Buttons */}
          <div className="flex space-x-3">
            {/* Reject Entry Button (Conditional) */}
            {isOpsAuthorized && (
              <button
                onClick={handleRejectClick}
                className="inline-flex items-center px-6 py-2.5 bg-btn-dark text-white font-semibold text-xs uppercase rounded-lg hover:bg-btn-dark-hover focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-150 shadow-md hover:shadow-lg transform hover:scale-[1.02] active:scale-[0.98]"
              >
                <HiBan className="w-4 h-4 mr-2" />
                REJECT ENTRY
              </button>
            )}

            {/* Proceed Button */}
            <button
              onClick={handleProceedClick}
              className="inline-flex items-center px-6 py-2.5 bg-btn-red text-white font-semibold text-xs uppercase rounded-lg hover:bg-btn-red-hover focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all duration-150 shadow-md hover:shadow-lg transform hover:scale-[1.02] active:scale-[0.98]"
            >
              <HiCheckCircle className="w-4 h-4 mr-2" />
              {isOpsAuthorized ? "Proceed to final exit" : "PROCEED TO AUTHORIZATION"}
            </button>
          </div>
        </div>
      )}

      {/* File Upload/Download Section (OPS Authorized Only) */}
      {isOpsAuthorized && (
        <div className="mt-4 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-sm font-bold text-gray-800 uppercase mb-4 flex items-center">
            <HiUpload className="w-5 h-5 mr-2 text-blue-600" />
            Document Management
          </h3>
          <div className="flex items-center space-x-4">
            {/* File Input */}
            <input
              type="file"
              className="flex-1 file-input file-input-bordered border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
              onChange={(e) => setFile(e.target.files[0])}
              accept="application/xls, application/xlsx"
            />

            {/* Upload Button */}
            <button
              className="inline-flex items-center px-6 py-2.5 bg-btn-red text-white font-semibold text-xs uppercase rounded-lg hover:bg-btn-red-hover focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all duration-150 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02] active:scale-[0.98]"
              disabled={!file || isUploading}
              onClick={handleFileUpload}
            >
              <HiUpload className="w-4 h-4 mr-2" />
              {isUploading ? "Uploading..." : "SAVE FILES"}
            </button>

            {/* Download Button */}
            <button
              className="inline-flex items-center px-6 py-2.5 bg-btn-dark text-white font-semibold text-xs uppercase rounded-lg hover:bg-btn-dark-hover focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-150 shadow-md hover:shadow-lg transform hover:scale-[1.02] active:scale-[0.98]"
              onClick={handleFileDownload}
            >
              <HiDownload className="w-4 h-4 mr-2" />
              DOWNLOAD FILES
            </button>
          </div>
        </div>
      )}

          {/* Display uploaded documents (read-only) */}
      {data && data?.documents && typeof data.documents === 'string' && (
        <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-md">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Uploaded Documents</h3>
          <div className="space-y-2">
            {(() => {
              try {
                const docs = JSON.parse(data.documents);
                return Array.isArray(docs) && docs.length > 0 ? (
                  docs.map((doc, index) => (
                    <div key={index} className="flex items-center gap-3 p-2 bg-white border border-gray-200 rounded">
                      <svg className="w-5 h-5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-700 truncate">{doc.filename || 'Unnamed file'}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 italic">No documents uploaded yet</p>
                );
              } catch (e) {
                return <p className="text-sm text-red-500">Error loading documents</p>;
              }
            })()}
          </div>
        </div>
      )}

      {data && data?.documents && typeof data.documents === 'object' && Array.isArray(data.documents) && (
        <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-md">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Uploaded Documents</h3>
          <div className="space-y-2">
            {data.documents.length > 0 ? (
              data.documents.map((doc, index) => (
                <div key={index} className="flex items-center gap-3 p-2 bg-white border border-gray-200 rounded">
                  <svg className="w-5 h-5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-700 truncate">{doc.filename || 'Unnamed file'}</p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500 italic">No documents uploaded yet</p>
            )}
          </div>
        </div>
      )}

      {/* Modals */}
      {finalClick && (
        <FinalExitProceed
          finalClick={finalClick}
          setFinalClick={setFinalClick}
          propData={propData}
          updateAPI={updateAPI}
          setAddOrEdit={setAddOrEdit}
        />
      )}

      {cancel && (
        <CancelPopUp
          cancel={cancel}
          setCancel={setCancel}
          propData={propData}
          APIdata={data}
          updateAPI={updateAPI}
          setAddOrEdit={setAddOrEdit}
          redirectPath={redirectPath}
          isLocalExit={isLocalExit}
          baseEndpoint={baseEndpoint}
        />
      )}
    </div>
  );
};

export default FinalExitDetails;
